// apps/share-web/src/offline/reconcile.ts
//
// Шесть событий стирания — отзыв доступа, вывод из команды, снятие песни с
// публикации, смена команды, переключение слов на Team only, снятие сетлиста
// с публикации (задача C5 волны G) — сведены к ОДНОЙ сверке:
//
//   успешный ответ /api/library = список того, что человеку положено иметь;
//   всё, что лежит в кэше под ним и чего в ответе нет, удаляется.
//
// ⚠️ ГЛАВНЫЙ ЗАМОК ВОЛНЫ И ЕГО МЕСТО. Сверка законна ТОЛЬКО после успешного
// ответа. После сетевого отказа она стёрла бы библиотеку целиком — то есть
// сломала бы ровно то, ради чего волна делается. Замок выражен СИГНАТУРОЙ:
// функция принимает `TeamLibrary[]`, которого при отказе не существует —
// `fetchLibrary` возвращает `{ok:false}` без поля `teams`. Передать сюда
// «результат отказа» нечем; подделать можно только пустым массивом, а это
// осмысленный вход («человека убрали отовсюду»), а не отказ.
// Сигнатура — половина замка, и СЕГОДНЯ ОНА ЕДИНСТВЕННАЯ. Вторая половина —
// что проводка зовёт эту функцию только из ветки `result.ok`; проводки пока
// не существует вовсе, поэтому тестом она не закрыта. Он появится вместе с
// ней (задача 11), и до тех пор замок наполовину отсутствует — это записано
// здесь, чтобы не считать его полным.
//
// Переключение слов на `Team only` в список событий НЕ входит, и это не
// упущение: visibility меняет доступ АНОНИМОВ по публичной ссылке, а кэш есть
// только у залогиненного члена команды — для него не изменилось ничего, и
// песня остаётся в ответе /api/library.

import { parseKey, type OfflineKind } from './offline-keys.js';
import { SETLIST_KEY_TEAM } from './setlist-offline.js';
import type { TeamLibrary } from '../library.js';

// Виды, которые сверка вообще СУДИТ. 'library' (снимок, которым мы сверяем —
// удалению по сверке не подлежит никогда) и 'me' (кэш вошедшего для шапки,
// офлайн-приёмка волны G — про него /api/library ничего не знает вовсе)
// остаются вне списка НАВСЕГДА, не временно.
//
// Список явный, а не `!== 'words'`: комментарий здесь предсказывал именно
// этот момент — «третий вид иначе молча попал бы под чужое правило». День
// настал раньше предсказанного: дефект 2 живой приёмки волны G завёл `'me'`,
// не предполагавшиеся составы сетлистов, и отрицание `!== 'words'` пережило
// его молча (`'me' !== 'words'`, то есть `continue`, то есть не тронуто —
// правильно, но случайно верно, не по имени). Задача C5 приносит `'setlist'`
// — ЧЕТВЁРТЫЙ вид, который сверка ОБЯЗАНА судить тем же правилом, что и
// 'words', и отрицание его больше не различило бы от 'me': оба «не words».
// Явный список снимает эту путаницу — каждый вид называется тем, чем он
// является сверке, а не тем, чем не является.
const RECONCILED_KINDS: ReadonlySet<OfflineKind> = new Set<OfflineKind>(['words', 'setlist']);

export function keysToDelete(cachedKeys: string[], teams: TeamLibrary[], userId: string): string[] {
  // Два разных факта, и путать их нельзя.
  //   `present`    — команда в ответе есть.
  //   `verifiable` — у неё приехал ДОСТОВЕРНЫЙ список (массив, пусть пустой)
  //                  — свой для слов, каждой команды отдельно.
  // `songTexts`/`setlists`/`textSetlists` необязательные (library.ts: воркер
  // и веб выкатываются порознь), поэтому откат воркера даёт успешный ответ
  // вообще без поля. Прочитать это как «ничего нет» значило бы стереть весь
  // кэш этого вида у всех команд человека на сцене. Отсутствие поля = «не
  // знаю» = не трогаем ничего этого вида у этой команды.
  //
  // `setlist` — ИСКЛЮЧЕНИЕ из правила «по команде» (исправление задачи D4):
  // кэш этого вида team-agnostic (`SETLIST_KEY_TEAM`, setlist-offline.ts —
  // её doc-комментарий несёт полное обоснование), поэтому судить его нужно
  // UNION'ом по ВСЕЙ библиотеке разом — сетлисты со звуком (`setlists[]`) И
  // слова-только строки со своим `setlistId` (`textSetlists[]`, задача D4
  // воркера), а не по совпадению команды в ключе (её там больше нет).
  const present = new Set<string>();
  const verifiableWords = new Map<string, Set<string>>();
  // `teams.length === 0` — «вывели отовсюду», настолько же достоверный ответ,
  // как непустой список: ноль команд ЗАВЕДОМО значит ноль легитимных
  // сетлистов, а не «не знаю» — иначе кэш этого вида пережил бы человека,
  // которого вывели из ПОСЛЕДНЕЙ его команды.
  let setlistsKnown = teams.length === 0;
  const verifiableSetlists = new Set<string>();
  for (const t of teams) {
    present.add(t.teamId);
    if (Array.isArray(t.songTexts)) verifiableWords.set(t.teamId, new Set(t.songTexts.map((w) => w.id)));
    if (Array.isArray(t.setlists)) {
      setlistsKnown = true;
      for (const s of t.setlists) verifiableSetlists.add(s.setlistId);
    }
    if (Array.isArray(t.textSetlists)) {
      setlistsKnown = true;
      for (const w of t.textSetlists) if (w.setlistId) verifiableSetlists.add(w.setlistId);
    }
  }

  const doomed: string[] = [];
  for (const key of cachedKeys) {
    const p = parseKey(key);
    if (!p) continue;
    // Чужие ключи не наше дело: сверка всегда про ОДНОГО человека.
    if (p.userId !== userId) continue;
    // Снимок библиотеки ('library') и кэш вошедшего ('me') сверке не
    // подлежат никогда — см. шапку файла.
    if (!RECONCILED_KINDS.has(p.kind)) continue;

    if (p.kind === 'setlist') {
      // Миграция (исправление задачи D4): записи, положенные ПРЕДЫДУЩИМ
      // коммитом под настоящей командой, — только на машинах разработки,
      // фича не выпущена, второго формата в природе не существует. Такой
      // ключ сносится безусловно, без сверки со списком: правильного ответа
      // «этот setlistId командой ли?» для НЕПРАВИЛЬНОГО сегмента ключа не
      // существует, а оставить его означало бы навсегда мёртвую запись,
      // которую никто и никогда не перезапишет (новый код всегда пишет под
      // `SETLIST_KEY_TEAM`).
      if (p.teamId !== SETLIST_KEY_TEAM) { doomed.push(key); continue; }
      // Ни одна команда не прислала ни `setlists`, ни `textSetlists` —
      // откат воркера или ответ без этих полей вовсе: судить нечем, тем же
      // правилом «не знаю — не трогаем», что и у слов ниже.
      if (!setlistsKnown) continue;
      if (!verifiableSetlists.has(p.id)) doomed.push(key);
      continue;
    }

    // 'words' — по команде, как и было.
    const ids = verifiableWords.get(p.teamId);
    if (!ids) {
      // Команда в ответе есть, а достоверного списка этого вида нет — судить
      // нечем, оставляем. Команды в ответе нет вовсе — это и есть «вывели из
      // команды», и тогда решает `present`, а не `ids`.
      if (!present.has(p.teamId)) doomed.push(key);
      continue;
    }
    // Песня, которой нет в достоверном списке команды — снята с публикации
    // (или доступ к ней отозван) с последнего успешного ответа.
    if (!ids.has(p.id)) doomed.push(key);
  }
  return doomed;
}
